/**
 * UATRunbookCard
 *   Renders the current iter's UAT_iter{X}.md as a clickable checklist.
 *   Parses numbered steps (lines like `1. …`, `2. …`) out of the
 *   markdown and renders each as a persistent checkbox wired to
 *   /api/release-ops/uat/{iter}/checks. State survives reloads
 *   (server-side per operator+iter+step).
 *   Verdict: "clear" when all steps checked, "fail" on load error,
 *   "info" otherwise.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    CheckCircle2, Loader2, Clipboard, ExternalLink, Move, Maximize2, Minimize2,
} from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import { FloatingPanel } from "@/components/site-admin/values-uat/FloatingPanel";

/**
 * Parse numbered steps from a UAT markdown body. Looks for lines like:
 *   1. Do the thing
 *   2. Do the next thing
 * Steps can span multiple lines (continuation lines are stripped of leading
 * indentation and folded into the same step). Returns [{id, n, text}].
 */
/**
 * Parses the "## Operator steps" section of a UAT markdown file.
 *
 * iter87.33 — Two important behaviors:
 *   1) Each returned step gets a UNIQUE `id` derived from its sequential
 *      position across the whole document. Previously the id was
 *      `step_${n}` where `n` was the display number — three sections
 *      that each started at `1.` shared the same id and their
 *      checkboxes ganged together (checking one flipped all three).
 *   2) When sections above steps use `### A) Title` / `### B) Title`
 *      headings, display numbers become hierarchical: `A.1`, `A.2`,
 *      `B.1`, etc. Flat UATs without section headings continue to
 *      show plain `1.`, `2.`, `3.` for backward compatibility.
 *
 * iter87.56 — Also accepts bold-label step syntax (`**A.1** text…`,
 * `**B.3** …`, `**1.2** …`) since that's how the agent has been
 * writing UATs since iter87.41. The bold label is used verbatim as
 * the display label.
 *
 * Continuation lines (bulleted lines under a numbered step with extra
 * indentation) are folded into the same step's text.
 *
 * Returns [{id, n, label, text}] where:
 *   - `id`     — stable slug for persistence (`step_<seq>`), unique.
 *   - `n`      — 1-based sequential index across the file.
 *   - `label`  — display string ("A.1", "1", …); what the operator sees.
 *   - `text`   — the step's markdown text.
 */
function parseUATSteps(markdown) {
    if (!markdown) return [];
    const lines = markdown.split("\n");
    const steps = [];
    let current = null;
    let inStepsSection = false;
    // Section state — reset on every `### ` heading we encounter under
    // the `## Operator steps` block.
    let sectionLetter = null;      // e.g. "A" when we see `### A) Foo`
    let sectionCounter = 0;         // 1..N — anonymous sections fallback
    let inSectionIndex = 0;         // step index within the current section
    for (const raw of lines) {
        if (/^##\s+(?=[^\n]*\bsteps?\b)/i.test(raw)) {
            inStepsSection = true;
            continue;
        }
        // Stop when we hit another top-level h2 that doesn't itself
        // contain "step(s)" — covers `## Result`, `## Rollback`, etc.
        if (inStepsSection && /^##\s+/i.test(raw) && !/^##\s+(?=[^\n]*\bsteps?\b)/i.test(raw)) {
            break;
        }
        if (!inStepsSection) continue;

        // `### A) …` (or `### A. …`) — hierarchical section marker.
        const h3 = raw.match(/^###\s+(.*)$/);
        if (h3) {
            if (current) { steps.push(current); current = null; }
            const lettered = h3[1].match(/^([A-Za-z])[).:]\s+/);
            if (lettered) {
                sectionLetter = lettered[1].toUpperCase();
            } else {
                sectionLetter = null;
                sectionCounter += 1;
            }
            inSectionIndex = 0;
            continue;
        }

        // iter87.56 — Also accept bold-label step syntax that the agent
        // has been authoring since iter87.41: `**A.1** text…`, `**B.3** …`,
        // `**1.2** …`. Prior parser only matched `1. text` (digit-period-
        // space), so every bold-label UAT silently rendered "no numbered
        // steps found". The bold label is authoritative — we use it
        // verbatim as the display label and also update sectionLetter so
        // any following `\d+. …` lines keep numbering consistent.
        const bold = raw.match(/^\*\*([A-Za-z0-9]+\.\d+)\*\*\s+(.*)$/);
        const m = bold || raw.match(/^(\d+)\.\s+(.*)$/);
        if (m) {
            if (current) steps.push(current);
            const n = steps.length + 1;              // sequential, unique
            inSectionIndex += 1;
            let label;
            if (bold) {
                label = bold[1];                      // "A.1", "B.3", "1.2" — as authored
                const letterMatch = bold[1].match(/^([A-Za-z])\./);
                if (letterMatch) sectionLetter = letterMatch[1].toUpperCase();
            } else {
                label = sectionLetter
                    ? `${sectionLetter}.${inSectionIndex}`
                    : sectionCounter > 0
                        ? `${sectionCounter}.${inSectionIndex}`
                        : String(m[1]);               // flat fallback
            }
            current = {
                id: `step_${n}`,                      // unique per file
                n,
                label,
                text: m[2].trim(),
            };
        } else if (current && raw.trim() !== "" && !raw.startsWith("#") && !raw.startsWith("---")) {
            // Continuation line. Trim leading indent + bullet markers.
            const cleaned = raw.replace(/^\s*-\s*/, "").trim();
            if (cleaned) current.text += " " + cleaned;
        }
    }
    if (current) steps.push(current);
    return steps;
}

export function UATRunbookCard({ environment = "preview", currentIter }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checks, setChecks] = useState({}); // step_id → bool
    const [savingStep, setSavingStep] = useState(null);

    const iter = currentIter || "";

    const refresh = useCallback(async () => {
        if (!iter) return;
        setLoading(true);
        setError(null);
        try {
            const runbook = await api.releaseOpsUatRunbook(iter);
            setData(runbook);
        } catch (e) {
            setError(e.response?.data?.detail || `No UAT runbook for iter${iter}`);
            setData(null);
        }
        try {
            const remote = await api.releaseOpsUatChecks(iter);
            const map = {};
            for (const c of remote.checks || []) map[c.step_id] = !!c.checked;
            setChecks(map);
        } catch {
            // non-fatal — leave checks empty
        }
        setLoading(false);
    }, [iter]);

    useEffect(() => { refresh(); }, [refresh]);

    const steps = useMemo(() => parseUATSteps(data?.markdown), [data]);
    const requiredCount = steps.length;
    const checkedCount = steps.filter((s) => checks[s.id]).length;
    const allChecked = requiredCount > 0 && checkedCount === requiredCount;

    // iter84.56.10 — Detect the canonical "pytest-attested only" marker.
    // When the UAT body contains this line AND there are no numbered steps,
    // the card renders a positive "fully attested" pill instead of the
    // grey "no numbered steps found" error. Marker spec lives in
    // /app/memory/uat/README.md.
    const pytestAttestedOnly = useMemo(
        () => /^\s*>\s*\*\*No post-deploy manual steps[^*\n]*pytest-attested only\.?\*\*/im.test(data?.markdown || ""),
        [data?.markdown],
    );

    const toggleCheck = async (step) => {
        const next = !checks[step.id];
        setSavingStep(step.id);
        setChecks((m) => ({ ...m, [step.id]: next }));
        try {
            await api.releaseOpsUatCheckUpsert(iter, step.id, next);
        } catch (e) {
            setChecks((m) => ({ ...m, [step.id]: !next }));
            toast.error(e.response?.data?.detail || "Save failed");
        }
        setSavingStep(null);
    };

    const copyRunbookUrl = () => {
        const url = `${window.location.origin}/api/release-ops/uat/${iter}`;
        navigator.clipboard.writeText(url).then(
            () => toast.success("Runbook URL copied"),
            () => toast.error("Copy failed"),
        );
    };

    // iter84.56.6.2 — Pop-out floating panel. Operator parks the
    // runbook to one side and walks the steps while clicking around
    // the rest of the page. Position + open-state persist per iter
    // in localStorage so the panel reappears where it was last.
    const popoutKey = `tcr.uat-popout.iter${iter || "x"}`;
    const [popped, setPopped] = useState(() => {
        if (typeof window === "undefined") return false;
        try {
            const raw = window.localStorage.getItem(popoutKey);
            if (!raw) return false;
            return !!JSON.parse(raw).open;
        } catch {
            return false;
        }
    });
    const togglePopped = (next) => {
        setPopped(next);
        try {
            const raw = window.localStorage.getItem(popoutKey);
            const prev = raw ? JSON.parse(raw) : {};
            window.localStorage.setItem(popoutKey, JSON.stringify({ ...prev, open: next }));
        } catch {
            // localStorage disabled — silent fallback (panel still works in-memory).
        }
    };

    // iter84.56.3 — Verdict colors the CollapsibleCard outline.
    // iter84.56.7-h — Amber while there are unchecked required steps so
    // the operator gets a visible "action needed" signal at-a-glance,
    // same outline grammar as the Values Engine / Readiness cards.
    const verdict = error
        ? "fail"
        : allChecked
            ? "clear"
            : (requiredCount > 0 ? "warn" : "info");

    const badge = !loading && !error && requiredCount > 0
        ? `${checkedCount}/${requiredCount} ${allChecked ? "complete" : "checked"}`
        : (iter ? `iter${iter}` : (environment === "production" ? "production" : "preview"));
    const badgeClass = allChecked
        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
        : iter
            ? "bg-volt/10 text-volt border-volt/40"
            : "bg-zinc-700/30 text-zinc-300 border-zinc-700";

    const headerExtra = data ? (
        <div className="flex items-center gap-1">
            <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); togglePopped(!popped); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); togglePopped(!popped); } }}
                data-testid="uat-runbook-popout-btn"
                data-popped={popped ? "true" : "false"}
                className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
                title={popped ? "Dock back into card" : "Pop out as floating panel (move to side, click around)"}
            >
                {popped ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                {popped ? "DOCK" : "POP OUT"}
            </span>
            <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); copyRunbookUrl(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); copyRunbookUrl(); } }}
                data-testid="uat-runbook-copy-url-btn"
                className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 cursor-pointer"
                title="Copy runbook API URL (bookmarkable)"
            >
                <ExternalLink className="w-3 h-3" /> URL
            </span>
        </div>
    ) : null;

    // The actual step-list body — extracted so both the inline card AND
    // the floating popout can render the same content.
    const body = (
        <div
            data-environment={environment}
            data-iter={iter}
            data-all-checked={allChecked ? "true" : "false"}
            data-testid="uat-runbook-progress"
        >
            {loading && (
                <p className="text-xs text-zinc-500 italic flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading runbook for iter{iter}…
                </p>
            )}
            {error && !loading && (
                <p className="text-xs text-rose-400" data-testid="uat-runbook-error">
                    {error}
                </p>
            )}
            {!loading && !error && steps.length === 0 && pytestAttestedOnly && (
                <div
                    className="border border-emerald-500/40 bg-emerald-500/10 p-3 rounded-sm text-emerald-200 text-xs flex items-start gap-2"
                    data-testid="uat-runbook-attested-only"
                >
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-400" />
                    <div>
                        <span className="font-bold uppercase tracking-wider text-[10px] block mb-0.5">
                            Fully attested · no manual UAT needed
                        </span>
                        <p className="text-emerald-200/80 text-[11px] leading-relaxed">
                            This iter shipped no user-visible changes. Agent pre-flight covers
                            all verification; nothing to walk through manually.
                        </p>
                    </div>
                </div>
            )}
            {!loading && !error && steps.length === 0 && !pytestAttestedOnly && (
                <p className="text-xs text-zinc-500 italic">
                    UAT runbook loaded but no numbered steps found. Add a section whose H2
                    header contains "step(s)" (e.g. `## Steps` or `## Targeted steps`), OR
                    mark the iter as pytest-only with{" "}
                    <code className="text-zinc-400">{`> **No post-deploy manual steps — iter is pytest-attested only.**`}</code>
                </p>
            )}
            {!loading && !error && steps.length > 0 && (
                <>
                    {data?.filename && (
                        <p className="text-[10px] text-zinc-500 font-mono mb-2">
                            {data.filename}
                        </p>
                    )}
                    <ul className="space-y-1.5" data-testid="uat-runbook-steps">
                        {steps.map((step) => {
                            const isChecked = !!checks[step.id];
                            const isSaving = savingStep === step.id;
                            return (
                                <li
                                    key={step.id}
                                    data-testid={`uat-runbook-step-${step.n}`}
                                    data-checked={isChecked ? "true" : "false"}
                                    className={`flex items-start gap-2 text-[11px] py-1.5 px-2 border rounded-sm transition-colors ${
                                        isChecked
                                            ? "border-emerald-500/40 bg-emerald-500/5"
                                            : "border-zinc-800 hover:border-zinc-700 bg-zinc-900/30"
                                    }`}
                                >
                                    <button
                                        type="button"
                                        role="checkbox"
                                        aria-checked={isChecked}
                                        onClick={() => toggleCheck(step)}
                                        disabled={isSaving}
                                        data-testid={`uat-runbook-step-${step.n}-checkbox`}
                                        className={`w-4 h-4 mt-0.5 shrink-0 border-2 rounded-sm flex items-center justify-center ${
                                            isChecked
                                                ? "border-emerald-400 bg-emerald-500/30"
                                                : "border-zinc-600 bg-zinc-900 hover:border-zinc-400"
                                        } disabled:opacity-50`}
                                    >
                                        {isSaving
                                            ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                            : isChecked
                                                ? <CheckCircle2 className="w-3 h-3 text-emerald-300" />
                                                : null}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <span className="text-zinc-300 leading-relaxed">
                                            <span className="font-mono text-zinc-500 mr-1">{step.label}.</span>
                                            {step.text}
                                        </span>
                                    </div>
                                    {/* iter84.57.2 — "Open ↗" button per step. If the step text
                                        contains a markdown link `[label](url)`, render an external-
                                        link button that opens it in a new tab. Lets the agent drop
                                        `[Open in production →](https://tinycrewrivalries.com/...)`
                                        into a step and the operator clicks once to land on the
                                        right page instead of typing the URL. */}
                                    {(() => {
                                        const m = step.text.match(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]+)\)/);
                                        if (!m) return null;
                                        const href = m[2];
                                        return (
                                            <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`uat-step-${step.n}-open-link`}
                                                className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-cyan-300 hover:text-cyan-200 border border-cyan-500/40 hover:border-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded-sm transition-colors"
                                                title={`Open ${href} in a new tab`}
                                            >
                                                <ExternalLink className="w-3 h-3" /> Open
                                            </a>
                                        );
                                    })()}
                                </li>
                            );
                        })}
                    </ul>
                    {allChecked && (
                        <p
                            data-testid="uat-runbook-all-checked-banner"
                            className="mt-3 text-[11px] text-emerald-300 font-bold flex items-center gap-1.5"
                        >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            All steps checked — you can tick the 
                            <code className="text-[10px] bg-zinc-800 px-1 rounded">uat_runbook_complete</code>
                             gate in the Runbook above.
                        </p>
                    )}
                </>
            )}
        </div>
    );

    return (
        <>
            <CollapsibleCard
                testId="uat-runbook-card"
                icon={Clipboard}
                title="Acceptance Checklist (preview)"
                badge={badge}
                badgeClass={badgeClass}
                verdict={verdict}
                headerExtra={headerExtra}
            >
                {popped ? (
                    <p className="text-[11px] text-zinc-500 italic flex items-center gap-2" data-testid="uat-runbook-popped-placeholder">
                        <Move className="w-3.5 h-3.5" />
                        Runbook is popped out as a floating panel. Drag it anywhere, or click <span className="font-bold text-volt">DOCK</span> in the header to bring it back here.
                    </p>
                ) : body}
            </CollapsibleCard>
            {popped && (
                <FloatingPanel
                    storageKey={popoutKey}
                    title={`Acceptance Checklist · iter${iter}`}
                    badge={badge}
                    badgeClass={badgeClass}
                    onClose={() => togglePopped(false)}
                >
                    {body}
                </FloatingPanel>
            )}
        </>
    );
}
